/**
 * Data models for Statement Import feature
 *
 * Feature: 001-statement-poc
 * All models are client-side TypeScript interfaces for Angular 20
 */

import { ICharacteristicMetaData } from "./i-characteristic-meta-data";

// ========================================
// Core Entities
// ========================================

/**
 * S3 metadata captured from FileStack's onSuccess callback after upload
 */
export interface FileStackMetadata {
	/** FileStack handle (unique file identifier) */
	handle: string;

	/** Hash-prefixed S3 storage key */
	key?: string;

	/** S3 bucket / cloud container name */
	container?: string;

	/** FileStack CDN URL for the uploaded file */
	url: string;

	/** Original file name as reported by FileStack */
	filename: string;

	/** MIME type as reported by FileStack */
	mimetype: string;

	/** File size in bytes as reported by FileStack */
	size: number;
}

/**
 * Represents a user-uploaded file in the import workflow
 */
export interface ImportedFile {
	/** Original file from browser File API */
	file: File;

	/** File name */
	fileName: string;

	/** File extension (xlsx, xls, or csv) */
	fileExtension: FileExtension;

	/** File size in bytes */
	fileSizeBytes: number;

	/** Timestamp when file was selected */
	uploadedAt: Date;

	/** File validation status */
	validationStatus: FileValidationStatus;

	/** Validation error message if status is Invalid */
	validationError?: string;

	/** S3 metadata from FileStack upload (populated after successful upload) */
	fileStackMetadata?: FileStackMetadata;
}

/**
 * Supported file extensions
 */
export enum FileExtension {
	XLSX = "xlsx",
	XLS = "xls",
	CSV = "csv"
}

/**
 * File validation status enumeration
 */
export enum FileValidationStatus {
	/** File has not been validated yet */
	Pending = "pending",

	/** File passed all validation checks */
	Valid = "valid",

	/** File failed validation (wrong type, too large, etc.) */
	Invalid = "invalid"
}

/**
 * Represents the data extracted from an imported file after parsing
 */
export interface ParsedFileData {
	/** Reference to the source file */
	sourceFile: ImportedFile;

	/** Parsed data rows (array of objects) */
	rows: DataRow[];

	/** Column definitions derived from data */
	columns: DataColumn[];

	/** Total number of rows in source file */
	totalRowCount: number;

	/** Number of rows in preview (may be limited) */
	previewRowCount: number;

	/** Whether preview is limited (not showing all rows) */
	isPreviewLimited: boolean;

	/** Parsing status */
	parsingStatus: ParsingStatus;

	/** Parsing error message if status is Failed */
	parsingError?: string;

	/** Time taken to parse file (milliseconds) */
	parseTimeMs: number;
}

/**
 * Parsing status enumeration
 */
export enum ParsingStatus {
	/** Parsing has not started */
	NotStarted = "not-started",

	/** Currently parsing file */
	InProgress = "in-progress",

	/** Parsing completed successfully */
	Success = "success",

	/** Parsing failed */
	Failed = "failed"
}

/**
 * Represents a single row of data from the parsed file
 */
export interface DataRow {
	/** Row index (0-based) */
	rowIndex: number;

	/** Row data as key-value pairs (column name → cell value) */
	data: Record<string, CellValue>;

	/** Original row number in source file (1-based, may differ from rowIndex if preview is limited) */
	sourceRowNumber: number;
}

/**
 * Cell value types supported in parsed data
 */
export type CellValue = string | number | Date | boolean | null;

/**
 * Represents a column definition for the preview grid
 */
export interface DataColumn {
	/** Unique column identifier (used as key in DataRow.data) */
	field: string;

	/** Display name for column header */
	headerName: string;

	/** Column data type (inferred from first few rows) */
	dataType: ColumnDataType;

	/** Column width in pixels (auto-sized or default) */
	width?: number;

	/** Whether column values can be edited in preview */
	editable: boolean;

	/** Sort direction if column is sorted */
	sort?: "asc" | "desc";

	/** Filter value if column is filtered */
	filter?: string;
}

/**
 * Column data type enumeration
 */
export enum ColumnDataType {
	String = "string",
	Number = "number",
	Date = "date",
	Boolean = "boolean",
	Mixed = "mixed"  // When column has multiple types
}

// ========================================
// Derived/Computed Values
// ========================================

/**
 * Summary information about the preview state (not persisted, computed on demand)
 */
export interface PreviewMetadata {
	/** Total number of columns */
	columnCount: number;

	/** Total rows in preview */
	visibleRowCount: number;

	/** Total rows in source file */
	totalRowCount: number;

	/** File size formatted for display (e.g., "9.5 MB") */
	fileSizeFormatted: string;

	/** Parse time formatted for display (e.g., "2.3s") */
	parseTimeFormatted: string;

	/** Whether preview shows all data */
	isCompletePreview: boolean;
}

// ========================================
// Validation Constants
// ========================================

/**
 * Maximum file size in bytes (10MB)
 */
export const MAX_FILE_SIZE_BYTES = 10485760;

/**
 * Maximum rows for preview (performance optimization)
 */
export const MAX_PREVIEW_ROWS = 10000;

/**
 * Validation error messages
 */
export const ValidationErrors = {
	FILE_TYPE: "File type not supported. Please upload an XLSX, XLS, or CSV file.",
	FILE_SIZE: "File size exceeds 10MB limit. Please upload a smaller file.",
	FILE_NAME: "File name is required.",
	NO_DATA: "File contains no data rows.",
	NO_COLUMNS: "File contains no columns."
} as const;

// ========================================
// V1.1 Entities (Added 2026-01-24)
// ========================================

/**
 * Represents a search match within the grid data (for find/replace functionality)
 */
export interface GridMatch {
	/** Row index in the data array (0-based) */
	rowIndex: number;

	/** Column field name where match was found */
	columnField: string;

	/** Original cell value containing the match */
	cellValue: string;

	/** Match index in global match array (0-based) */
	matchIndex: number;

	/** Page number where match is located (0-based) */
	pageNumber: number;

	/** Whether this is the currently selected/highlighted match */
	isCurrent: boolean;
}

/**
 * Represents the current state of the find/replace operation
 */
export interface FindReplaceState {
	/** Current search term (case-insensitive) */
	searchTerm: string;

	/** Replacement text */
	replacementTerm: string;

	/** All matches found across all pages */
	matches: GridMatch[];

	/** Index of currently selected match (0-based) */
	currentMatchIndex: number;

	/** Total number of matches found */
	totalMatches: number;

	/** Whether find operation is active */
	isActive: boolean;

	/** Timestamp of last search */
	lastSearchTimestamp: Date;
}

/**
 * Represents the current pagination configuration and state
 */
export interface PaginationState {
	/** Current page number (0-based) */
	currentPage: number;

	/** Number of rows per page */
	pageSize: number;

	/** Total number of pages */
	totalPages: number;

	/** Total number of rows across all pages */
	totalRows: number;

	/** Start row index for current page (0-based, inclusive) */
	startRow: number;

	/** End row index for current page (0-based, exclusive) */
	endRow: number;

	/** Available page size options */
	pageSizeOptions: number[];
}

/**
 * Represents a single cell edit operation (for tracking edit history)
 */
export interface CellEdit {
	/** Row index in the data array (0-based) */
	rowIndex: number;

	/** Column field name */
	columnField: string;

	/** Original value before edit */
	oldValue: CellValue;

	/** New value after edit */
	newValue: CellValue;

	/** Timestamp when edit occurred */
	editedAt: Date;

	/** Whether this edit has been committed */
	isCommitted: boolean;
}


export interface SystemError {
	rowId: string;
	originalRowIndex: number;
	columnField: string;
	value: any;
	errorMessage: string;
}

export interface CellValidationError {
	rowId: string;
	originalRowIndex: number;
	columnField: string;
	value: any;
	errorMessage: string;
	validationType: "required" | "lov" | "money" | "number" | "date" | "range" | "custom" | "identifier" | "currency";
	isColumnLevel?: boolean;
}

/**
 * The validation rule applied to a single preview-grid column, derived from the mapped system
 * column's metadata (data type, required flag, LOV source) or from identifier/custom checks.
 */
export interface ValidationRule {
	type: "lov" | "money" | "number" | "date" | "range" | "custom" | "required" | "identifier";
	required?: boolean;
	columnLabel?: string;
	charValueSourceID?: string;  // For LOV validation
	validValues?: string[];  // Cached LOV labels
	validValueIds?: number[];  // Cached LOV IDs
	pattern?: RegExp;  // For money/number validation
	min?: number;  // For range validation
	max?: number;  // For range validation
	customValidator?: (value: any) => string | null;
}

// ========================================
// V2.0 Entities (002-column-mapping) - Added 2026-01-24
// ========================================

/**
 * Represents a worksheet/sheet within an Excel workbook
 */
export interface Worksheet {
	/** Sheet name as displayed in Excel */
	sheetName: string;

	/** Sheet index (0-based) within the workbook */
	sheetIndex: number;

	/** Number of rows in the sheet */
	rowCount: number;

	/** Number of columns in the sheet */
	columnCount: number;

	/** True for first sheet or CSV implicit sheet */
	isDefault: boolean;
}

/**
 * Represents the selected header row configuration
 */
export interface HeaderRowSelection {
	/** Header row number (1-50, user-facing 1-based) */
	rowNumber: number;

	/** Column names extracted from the header row */
	columnNames: string[];

	/** Whether duplicate column names were detected */
	hasDuplicates: boolean;

	/** Original names that were duplicated (before deduplication) */
	duplicateColumns?: string[];

	/** Whether the selected row is completely empty */
	isEmpty: boolean;

	/** Whether the selected row is beyond the data range */
	isOutOfRange: boolean;
}

/**
 * Represents a column detected from the imported file
 */
export interface ImportedColumn {
	/** Original name from header row */
	originalName: string;

	/** Display name with _2, _3 suffixes if duplicate */
	displayName: string;

	/** 0-based column index (A=0, B=1, etc.) */
	ordinalPosition: number;

	/** Optional preview of first 3-5 values */
	dataSample?: CellValue[];
}

/**
 * Represents a system column definition (predefined financial data field)
 */
export interface SystemColumn {
	/** Unique identifier (e.g., "invoice_number") */
	id: string;

	/** Human-readable name (e.g., "Invoice Number") */
	displayName: string;

	/** Expected data type for this system column */
	dataType: ColumnDataType;

	/** Whether this column must be mapped (cannot be empty) */
	isRequired: boolean;

	/** Help text or description for users */
	description?: string;

	/** Synonyms for fuzzy matching (e.g., ["Inv #", "Inv No"] for "Invoice Number") */
	alternativeNames?: string[];
}

export interface ModuleIdColumnDetail {

	moduleCharTypeId: number;

	moduleName: string;

	isRequired: boolean;

	identifierColumns: ICharacteristicMetaData[];
}

/**
 * Represents a mapping between a system column and an imported column
 * v2: System-column-led - one entry per system column, importedColumn is nullable
 */
export interface ColumnMapping {
	/** The imported column mapped to this system column (null if unmapped) */
	importedColumn: ImportedColumn | null;

	/** The system column (row identity, always set) */
	systemColumn: ICharacteristicMetaData;

	/** How this mapping was created */
	mappingType: MappingType;

	/** Confidence score 0-100 (only for auto-matched) */
	confidenceScore?: number;

	/** Optional default value for this system column */
	defaultValue?: string;

	/** Timestamp when mapping was created/modified */
	createdAt: Date;

	/** For showing a module association ID field */
	moduleIdDetail?: ModuleIdColumnDetail;

	isAssociationColumn?: boolean;
}

/**
 * Mapping type enumeration
 */
export enum MappingType {
	/** Automatically matched by fuzzy matching algorithm */
	AutoMatched = "auto-matched",

	/** Manually mapped by user */
	ManualMapped = "manual-mapped",

	/** Not yet mapped to any system column */
	Unmapped = "unmapped",

	/** Smartly generated mapping */
	SmartMapped = "smart-mapped"
}

export interface ValueLevelMapping {
	value: string;
	mappingType: MappingType.AutoMatched | MappingType.ManualMapped | MappingType.Unmapped | MappingType.SmartMapped;
}

export type ColumnMatchTier = "exact" | "fuzzy";

/**
 * Represents a match result (potential match between imported and system column)
 */
export interface FuzzyMatchResult {
	/** The imported column being matched */
	importedColumn: ImportedColumn;

	/** The system column candidate */
	systemColumn: ICharacteristicMetaData;

	/** Confidence score 0-100 */
	confidenceScore: number;

	/** The tier that produced the match */
	matchTier: ColumnMatchTier;

	/** Explanation of why match was made (e.g., "Exact match") */
	matchReason?: string;
}

/**
 * Configuration state for the entire Configuration step
 */
export interface ConfigurationState {
	/** Selected worksheet (null before selection) */
	selectedWorksheet: Worksheet | null;

	/** Header row selection (null before selection) */
	headerRowSelection: HeaderRowSelection | null;

	/** Imported columns extracted from header row */
	importedColumns: ImportedColumn[];

	/** Current column mappings */
	columnMappings: ColumnMapping[];

	/** Fuzzy match results from auto-matching */
	fuzzyMatchResults: FuzzyMatchResult[];

	/** Current validation status */
	validationStatus: ValidationStatus;

	/** Validation error messages */
	validationErrors: string[];

	/** The template ID selected by the user in the column mapping panel */
	selectedTemplateId: number | null;

	moduleTemplateIdMap: Map<number, number[]>;

	/** Value-level mappings for LOV columns: "columnName_systemColumnId" -> { importedValue -> mappedValue } */
	valueLevelMappings?: Record<string, Record<string, ValueLevelMapping>>;
}

/**
 * Validation status enumeration
 */
export enum ValidationStatus {
	/** Validation has not been performed yet */
	NotValidated = "not-validated",

	/** All required system columns are mapped */
	Valid = "valid",

	/** One or more required system columns are unmapped */
	Invalid = "invalid"
}

// ========================================
// V3.0 Entities (001-mapping-profile) - Added 2026-01-25
// ========================================

/**
 * Represents a saved column mapping profile
 * Stored in browser Local Storage as JSON
 */
export interface MappingProfile {
	/**
	 * Unique identifier (timestamp-based: `profile_${Date.now()}_${randomSuffix}`)
	 * Example: "profile_1706198400000_a3f2"
	 */
	id: string;

	/**
	 * User-provided profile name
	 * Validation: 3-255 characters, required
	 * Used for display in profile list and selection dropdown
	 */
	name: string;

	/**
	 * Optional user-provided description
	 * Validation: max 1000 characters
	 * Helps users remember file type/source for profile
	 */
	description?: string;

	/**
	 * ISO 8601 timestamp when profile was created
	 * Immutable after creation
	 * Used for sorting (newest first) and display
	 */
	createdAt: string; // ISO 8601 format for JSON serialization

	/**
	 * ISO 8601 timestamp of last modification to name/description
	 * Updated only on metadata changes, not when profile is applied
	 */
	lastModified: string; // ISO 8601 format

	/**
	 * Array of column mappings captured in this profile
	 * Minimum 1 mapping required (empty profiles rejected)
	 * Ordered by original ordinal position for consistency
	 */
	mappings: ProfileColumnMapping[];
}

/**
 * Column mapping within a profile (serializable subset of ColumnMapping)
 * Stores only the essential information needed to recreate mappings
 */
export interface ProfileColumnMapping {
	/**
	 * Imported column name from original file
	 * Exact match used when applying profile (case-sensitive)
	 * Example: "Revenue", "License Fee", "Payment Date"
	 */
	importedColumnName: string;

	/**
	 * System column ID this was mapped to (characteristicID)
	 * Used to look up ICharacteristicMetaData when applying profile
	 * Example: 123, 456, 789
	 */
	systemColumnId: number;

	/**
	 * System column name (display name for reference)
	 * Used for preview and debugging
	 * Example: "Amount", "Title", "Start Date"
	 */
	systemColumnName: string;

	/**
	 * Mapping type (auto-matched or manual-mapped)
	 * Preserved from original mapping for audit/reference
	 * When profile is applied, all become ManualMapped
	 */
	mappingType: MappingType.AutoMatched | MappingType.ManualMapped;

	/**
	 * Original ordinal position (0-based column index)
	 * Used for display order and debugging
	 * Example: 0 (first column), 5 (sixth column)
	 */
	ordinalPosition: number;

	/**
	 * Optional default value persisted from the mapping
	 * v2: Added for default value support
	 */
	defaultValue?: string;
	moduleCharTypeId?: number;
	valueLevelMappings?: Record<string, string>;
}

/**
 * Root storage schema for profile persistence
 * Single JSON object containing all profiles and metadata
 */
export interface ProfileStorageSchema {
	/**
	 * Schema version for migrations
	 * v1: Original schema without default values
	 * v2: Added defaultValue to ProfileColumnMapping
	 */
	version: 1 | 2;

	/**
	 * Array of all saved profiles
	 * Ordered by createdAt descending (newest first) for display
	 */
	profiles: MappingProfile[];

	/**
	 * Storage-level metadata
	 */
	metadata: {
		/**
		 * ISO 8601 timestamp when storage was first initialized
		 */
		createdAt: string;

		/**
		 * ISO 8601 timestamp of last modification (save/delete/update)
		 */
		lastModified: string;
	};
}

/**
 * Backend response shape for mapping profile endpoints.
 * Keep this API contract in models so services stay focused on mapping behavior.
 */
export interface MappingProfileApiResponse {
	creId: number;
	creTms: string;
	divId: number;
	jsonVal?: string | null;
	lstUpdId?: number | null;
	lstUpdTms?: string | null;
	mappingProfileId: number;
	profileName: string;
	description?: string | null;
}

// ========================================
// Type Guards for Profile Validation
// ========================================

/**
 * Type guard for MappingProfile
 * Validates structure and required fields at runtime
 */
export function isMappingProfile(obj: any): obj is MappingProfile {
	return (
		typeof obj === "object" &&
		obj !== null &&
		typeof obj.id === "string" &&
		obj.id.length > 0 &&
		typeof obj.name === "string" &&
		obj.name.length >= 3 &&
		obj.name.length <= 255 &&
		(obj.description === undefined || typeof obj.description === "string") &&
		(!obj.description || obj.description.length <= 1000) &&
		typeof obj.createdAt === "string" &&
		typeof obj.lastModified === "string" &&
		Array.isArray(obj.mappings) &&
		obj.mappings.length > 0 &&
		obj.mappings.every(isProfileColumnMapping)
	);
}

/**
 * Type guard for ProfileColumnMapping
 * Validates structure of individual mappings
 */
export function isProfileColumnMapping(obj: any): obj is ProfileColumnMapping {
	return (
		typeof obj === "object" &&
		obj !== null &&
		typeof obj.importedColumnName === "string" &&
		typeof obj.systemColumnId === "number" &&
		obj.systemColumnId > 0 &&
		typeof obj.systemColumnName === "string" &&
		obj.systemColumnName.length > 0 &&
		(obj.mappingType === MappingType.AutoMatched || obj.mappingType === MappingType.ManualMapped) &&
		typeof obj.ordinalPosition === "number" &&
		obj.ordinalPosition >= -1 &&
		(obj.defaultValue === undefined || typeof obj.defaultValue === "string")
	);
}

/**
 * Type guard for ProfileStorageSchema
 * Validates root storage structure
 */
export function isProfileStorageSchema(obj: any): obj is ProfileStorageSchema {
	return (
		typeof obj === "object" &&
		obj !== null &&
		(obj.version === 1 || obj.version === 2) &&
		Array.isArray(obj.profiles) &&
		obj.profiles.every(isMappingProfile) &&
		typeof obj.metadata === "object" &&
		typeof obj.metadata.createdAt === "string" &&
		typeof obj.metadata.lastModified === "string"
	);
}

// ========================================
// Conversion Functions for Profile Mappings
// ========================================

/**
 * Converts ColumnMapping to ProfileColumnMapping for storage
 * v2: Handles nullable importedColumn and includes defaultValue
 * Only mappings with an imported column or a default value should be saved
 */
export function toProfileColumnMapping(mapping: ColumnMapping): ProfileColumnMapping {
	return {
		importedColumnName: mapping.importedColumn?.displayName ?? "",
		systemColumnId: mapping.systemColumn.characteristicID,
		systemColumnName: mapping.systemColumn.label,
		mappingType: mapping.importedColumn != null
			? (mapping.mappingType === MappingType.AutoMatched ? MappingType.AutoMatched : MappingType.ManualMapped)
			: MappingType.ManualMapped,
		ordinalPosition: mapping.importedColumn?.ordinalPosition ?? -1,
		defaultValue: mapping.defaultValue,
		moduleCharTypeId: mapping.moduleIdDetail?.moduleCharTypeId
	};
}

/**
 * Builds an empty ParsedFileData placeholder for opening the preview grid outside the import
 * wizard (e.g. reviewing a past import's errors), before real row data is wired up from an API.
 */
export function createEmptyParsedFileData(fileName: string): ParsedFileData {
	return {
		sourceFile: {
			file: null as unknown as File,
			fileName,
			fileExtension: FileExtension.CSV,
			fileSizeBytes: 0,
			uploadedAt: new Date(),
			validationStatus: FileValidationStatus.Valid
		},
		rows: [],
		columns: [],
		totalRowCount: 0,
		previewRowCount: 0,
		isPreviewLimited: false,
		parsingStatus: ParsingStatus.Success,
		parseTimeMs: 0
	};
}

/**
 * Builds an empty ConfigurationState placeholder (no column mappings) to pair with
 * createEmptyParsedFileData when opening the preview grid outside the import wizard.
 */
export function createEmptyConfigurationState(): ConfigurationState {
	return {
		selectedWorksheet: null,
		headerRowSelection: null,
		importedColumns: [],
		columnMappings: [],
		fuzzyMatchResults: [],
		validationStatus: ValidationStatus.Valid,
		validationErrors: [],
		selectedTemplateId: null,
		moduleTemplateIdMap: new Map()
	};
}

/**
 * Converts ProfileColumnMapping back to ColumnMapping during profile load
 * v2: Handles nullable importedColumn and restores defaultValue
 * Returns null only if system column not found (imported column may be null for default-only entries)
 */
export function fromProfileColumnMapping(
	profileMapping: ProfileColumnMapping,
	importedColumns: ImportedColumn[],
	systemColumns: ICharacteristicMetaData[]
): ColumnMapping | null {
	const systemCol = systemColumns.find(
		col => col.characteristicID === profileMapping.systemColumnId
	);

	if (!systemCol) {
		console.warn(`Cannot recreate mapping: system column "${profileMapping.systemColumnName}" (ID: ${profileMapping.systemColumnId}) not found`);
		return null;
	}

	const importedCol = profileMapping.importedColumnName
		? importedColumns.find(col => col.displayName === profileMapping.importedColumnName) ?? null
		: null;

	return {
		importedColumn: importedCol,
		systemColumn: systemCol,
		mappingType: importedCol ? profileMapping.mappingType : MappingType.Unmapped,
		defaultValue: profileMapping.defaultValue,
		createdAt: new Date()
	};
}
